import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faSpinner, 
  faPauseCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';

export interface ExecutionStep {
  id: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
}

interface ExecutionPlanProps {
  steps: ExecutionStep[];
  className?: string;
}

export const ExecutionPlan: React.FC<ExecutionPlanProps> = ({ steps, className = '' }) => {
  const getStatusIcon = (status: ExecutionStep['status']) => {
    switch (status) {
      case 'completed':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-green-400" />;
      case 'in_progress':
        return <FontAwesomeIcon icon={faSpinner} className="text-yellow-400 animate-spin" />;
      case 'failed':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-red-400" />;
      default:
        return <FontAwesomeIcon icon={faPauseCircle} className="text-gray-400" />;
    }
  };

  const getStatusColor = (status: ExecutionStep['status']) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'in_progress':
        return 'text-yellow-400 font-medium';
      case 'failed':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  if (!steps || steps.length === 0) {
    return null;
  }

  return (
    <div className={`bg-gray-900 border border-gray-700 rounded-lg p-4 ${className}`}>
      <div className="flex items-center mb-3">
        <span className="text-sm font-medium text-blue-400">📋 Execution Plan</span>
      </div>
      <div className="space-y-2">
        {steps.map((step, index) => (
          <div 
            key={step.id} 
            className={`flex items-center space-x-3 p-2 rounded transition-colors ${
              step.status === 'in_progress' ? 'bg-gray-800' : ''
            }`}
          >
            <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
              {getStatusIcon(step.status)}
            </span>
            <span className={`text-sm ${getStatusColor(step.status)} flex-1`}>
              {step.name}
            </span>
            {step.status === 'in_progress' && (
              <span className="text-xs text-yellow-400 bg-yellow-400/10 px-2 py-1 rounded">
                Processing...
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExecutionPlan;